// officeDokument.ts — „Neues Dokument": leeres Word/Excel anlegen und im
// Office-Editor öffnen (User 28.09.2026: „neues Dokument und dann fragen Excel
// oder Word und Namen eingeben").
//
// Gemeinsam genutzt vom „+“-Menü im App-Header (NeuAnlageDialog) und vom
// Vorgang (NeuesOfficeDokumentDialog). Die Datei erzeugt der Server
// (`POST /documents/office/neu`), geöffnet wird sie auf derselben Vollbild-
// Route wie jede andere Office-Datei: `/dokumente/:id/bearbeiten`.

import * as React from "react";
import { officeEditingService } from "../../services/officeEditingService";

export type OfficeArt = "docx" | "xlsx";

export const OFFICE_ARTEN: ReadonlyArray<{ art: OfficeArt; label: string; kurz: string; hinweis: string }> = [
  { art: "docx", label: "Word-Dokument", kurz: "Word", hinweis: "Neu und leer, .docx" },
  { art: "xlsx", label: "Excel-Tabelle", kurz: "Excel", hinweis: "Neu und leer, .xlsx" },
];

/**
 * Der Name, wie ihn der Server speichern wird — nur zur Anzeige („wird
 * gespeichert als …"). Die verbindliche Bereinigung macht der Server.
 * Eine mitgetippte gleiche Endung wird nicht verdoppelt.
 */
export const nameMitEndung = (name: string, art: OfficeArt): string => {
  let n = name.trim().replace(/\\/g, "/");
  n = n.slice(n.lastIndexOf("/") + 1).trim();
  const endung = `.${art}`;
  if (n.toLowerCase().endsWith(endung)) n = n.slice(0, -endung.length).trim();
  return n ? `${n}${endung}` : "";
};

/** Ist der Name brauchbar? Leer oder nur Pfad/Endung genügt nicht. */
export const nameGueltig = (name: string, art: OfficeArt): boolean => nameMitEndung(name, art) !== "";

/**
 * Tab für den Editor SOFORT im Klick öffnen, die Adresse erst nach dem
 * Anlegen setzen.
 *
 * Ein `window.open` nach einem `await` zählt für den Browser nicht mehr als
 * Folge des Klicks — Edge blockiert es „leise“ mit einem kleinen Symbol in der
 * Adressleiste, das unsere Nutzer nicht bemerken. Deshalb öffnet der Klick den
 * Tab leer, und `editorOeffnen` lenkt ihn um. Kommt kein Tab zustande (strenge
 * Einstellung), versucht `editorOeffnen` es noch einmal regulär.
 */
export const editorTabVorbereiten = (hinweis = "Dokument wird angelegt …"): Window | null => {
  try {
    const tab = window.open("", "_blank");
    if (!tab) return null;
    try {
      tab.document.title = hinweis;
      tab.document.body.style.cssText =
        "font:15px system-ui,sans-serif;color:#5B6570;display:grid;place-items:center;height:100vh;margin:0";
      tab.document.body.textContent = hinweis;
    } catch {
      /* fremde Einstellungen — der leere Tab genügt */
    }
    return tab;
  } catch {
    return null;
  }
};

export const editorPfad = (dokumentId: number): string => `/dokumente/${dokumentId}/bearbeiten`;

/** Den vorbereiteten Tab auf den Editor lenken — oder einen neuen öffnen. */
export const editorOeffnen = (dokumentId: number, tab: Window | null): void => {
  const pfad = editorPfad(dokumentId);
  if (tab && !tab.closed) {
    tab.location.replace(pfad);
    return;
  }
  window.open(pfad, "_blank", "noopener");
};

/** Anlegen ist schiefgegangen: den leeren Tab wieder zumachen. */
export const editorTabVerwerfen = (tab: Window | null): void => {
  try {
    if (tab && !tab.closed) tab.close();
  } catch {
    /* nichts zu tun */
  }
};

/**
 * Gibt es auf diesem Server den Office-Editor? Ohne ihn ergäbe „Neues
 * Dokument" eine leere Datei, die sich nicht öffnen lässt — dann bieten wir es
 * gar nicht erst an. `null` = Antwort steht noch aus.
 */
export function useOfficeVerfuegbar(): boolean | null {
  const [verfuegbar, setVerfuegbar] = React.useState<boolean | null>(null);
  React.useEffect(() => {
    let aktiv = true;
    void officeEditingService.isEnabled().then((v) => aktiv && setVerfuegbar(v));
    return () => {
      aktiv = false;
    };
  }, []);
  return verfuegbar;
}
